import { CHARACTER, HAIRS, type HeldTool, type Look, type ToolKind, type ToolTier } from "@dyson/shared";
import { any, box, cap, disc, pixels, poly, stroke, type Pen } from "./pixelArt";
import { RigModel, hsl, idHash, mix, scale, WHITE, type Color, type Palette, type Part, type Pose, type Shape } from "./Rig";

/**
 * The crew: the players, as a rig. A sky-sailor in a long coat in their own
 * colour, a scarf, knee boots, a steampack on their back (if they wear one) and whatever tool
 * is in hand. Built in figure metres (1.7 tall, feet at the origin, facing
 * +x) and scaled up to CHARACTER.height by whoever draws it.
 *
 * Each part is pixel art (see pixelArt.ts), painted once at the tiles' own
 * 32 pixels a block, lit from the upper left and outlined like the tiles;
 * the rig only moves the pieces. Every limb hangs from a round end sunk in
 * what it hangs from, so nothing gaps as it swings; the scarf bends (see Bend).
 */

/** The figure is built 1.7 tall and scaled up to CHARACTER.height; this converts real speeds back to its own. */
export const BODY_SCALE = 1.7 / CHARACTER.height;

// Proportions, in figure metres.
const HIP_Y = 0.62;
const THIGH = 0.32;
const SHIN = 0.3;
const SHOULDER_Y = 0.55; // above the hips
const NECK_Y = 0.62; // above the hips
const UPPER_ARM = 0.3;
const FOREARM = 0.26;
const SWING_TIME = 0.32;
/** Wrist turn that holds the tool angled a little above where the arm points. */
const AIM_WRIST = 0.5 - Math.PI;

/** How each build changes the figure: the coat wider or narrower, the body a touch taller or shorter. */
const BUILDS: Record<Look["body"], { w: number; h: number }> = {
  standard: { w: 1, h: 1 },
  stocky: { w: 1.2, h: 0.94 },
  slim: { w: 0.85, h: 1.04 },
};

/** How the hair is worn: chosen, like the hat (the colour comes with the name). */
export type HairStyle = Look["hair"];
export const HAIR_STYLES: readonly HairStyle[] = HAIRS;

// ------------------------------------------------------------ the figure

/** Pixels a figure metre: the tiles' 32 a block, at 2 blocks a metre. */
const PPU = 64;

type Pt = readonly [number, number];

/** A part's pixel art over the box [x0, y0, x1, y1] (figure metres from its pivot). */
const art = (x0: number, y0: number, x1: number, y1: number, paint: (pen: Pen) => void): Shape => ({ color: "", form: pixels({ minX: x0, minY: y0, maxX: x1, maxY: y1 }, PPU, paint) });

/** Points scaled across by `w` and up by `h` (a build's coat). */
const sized = (pts: readonly Pt[], w: number, h = 1): Pt[] => pts.map(([x, y]) => [x * w, y * h]);

const FACE: Pt[] = [[-0.06, 0.1], [0.06, 0.05], [0.15, 0.07], [0.19, 0.12], [0.2, 0.19], [0.245, 0.21], [0.235, 0.245], [0.21, 0.26], [0.19, 0.32], [0.02, 0.33]];
const HAIR: Pt[] = [[0.2, 0.33], [0.2, 0.4], [0.15, 0.45], [0.06, 0.48], [-0.05, 0.48], [-0.14, 0.45], [-0.2, 0.37], [-0.22, 0.27], [-0.2, 0.18], [-0.17, 0.1], [-0.09, 0.13], [-0.1, 0.2], [-0.07, 0.26], [-0.03, 0.29], [0.05, 0.31], [0.12, 0.3], [0.13, 0.33], [0.17, 0.29]];

/** The head, from the base of the neck: face to +x, hair over the top and back, the hat on top. */
function head(hair: HairStyle, hat: Look["hat"]): Shape {
  return art(-0.46, -0.06, 0.42, 0.64, (p) => {
    p.lit("skin", cap(0, -0.03, 0.02, 0.14, 0.13));
    p.lit("skin", any(disc(0, 0.25, 0.2, 0.215), poly(FACE)));
    // Ear, cheek, mouth, eye with a glint.
    p.flat("skin", disc(-0.045, 0.205, 0.034, 0.05), 1);
    p.flat("skin", box(-0.045, 0.185, -0.03, 0.225), 0);
    p.flat("blush", disc(0.12, 0.165, 0.032, 0.024));
    p.flat("mouth", cap(0.15, 0.125, 0.185, 0.13, 0.016));
    p.flat("eye", disc(0.135, 0.25, 0.03, 0.042));
    p.flat("glint", box(0.14, 0.258, 0.156, 0.274));
    // Hair, a brow, and the tail or bun.
    if (hair === "tail") {
      p.lit("hair", poly([[-0.17, 0.3], [-0.26, 0.31], [-0.32, 0.25], [-0.33, 0.15], [-0.3, 0.05], [-0.26, 0.04], [-0.27, 0.13], [-0.24, 0.19], [-0.15, 0.2]]));
      p.flat("accentDark", disc(-0.2, 0.27, 0.03, 0.04));
    }
    if (hair === "bun") p.lit("hair", disc(-0.17, 0.43, 0.09));
    p.lit("hair", poly(HAIR));
    p.flat("hair", cap(0.09, 0.31, 0.18, 0.315, 0.025), 1);
    hatArt(p, hat);
  });
}

function hatArt(p: Pen, hat: Look["hat"]): void {
  switch (hat) {
    case "cap":
      // A flat cap with a short peak, in the accent colour.
      p.lit("accent", poly([[-0.22, 0.36], [-0.21, 0.46], [-0.12, 0.52], [0.02, 0.54], [0.15, 0.52], [0.22, 0.46], [0.24, 0.39]]));
      p.flat("accent", poly([[-0.22, 0.36], [0.24, 0.39], [0.24, 0.42], [-0.22, 0.4]]), 1);
      p.lit("accentDark", poly([[0.14, 0.4], [0.37, 0.37], [0.38, 0.35], [0.35, 0.335], [0.14, 0.355]]));
      return;
    case "tricorn":
      // Dark felt, the brim cocked up front and back, brass along its edge.
      p.lit("felt", poly([[-0.19, 0.37], [-0.17, 0.5], [-0.08, 0.57], [0.02, 0.59], [0.13, 0.57], [0.2, 0.5], [0.21, 0.38]]));
      p.lit("felt", poly([[-0.31, 0.46], [-0.2, 0.38], [0.02, 0.35], [0.22, 0.37], [0.33, 0.45], [0.22, 0.42], [0.02, 0.425], [-0.2, 0.42]]));
      p.flat("brass", stroke([[-0.3, 0.45], [-0.2, 0.375], [0.02, 0.35], [0.22, 0.365], [0.32, 0.44]], 0.022));
      return;
    case "goggles":
      // A leather strap round the brow, a brass-rimmed lens pushed up on it.
      p.lit("leather", poly([[-0.21, 0.32], [0, 0.37], [0.21, 0.35], [0.21, 0.4], [0, 0.43], [-0.21, 0.37]]));
      p.lit("brass", disc(0.16, 0.38, 0.075, 0.07));
      p.flat("brassDark", disc(0.17, 0.375, 0.056, 0.052));
      p.lit("glass", disc(0.17, 0.378, 0.044, 0.041));
      p.flat("glint", box(0.145, 0.39, 0.165, 0.41));
      return;
    case "bandana":
      // Tied over the top, the knot at the back with its ends hanging.
      p.flat("accentDark", poly([[-0.22, 0.34], [-0.32, 0.26], [-0.34, 0.13], [-0.28, 0.15], [-0.26, 0.26], [-0.2, 0.3]]));
      p.lit("accent", poly([[-0.22, 0.34], [-0.36, 0.34], [-0.43, 0.24], [-0.37, 0.21], [-0.32, 0.29], [-0.21, 0.3]]));
      p.lit("accent", poly([[-0.21, 0.33], [-0.2, 0.43], [-0.1, 0.49], [0.04, 0.5], [0.15, 0.47], [0.22, 0.36], [0.1, 0.39], [0, 0.39], [-0.1, 0.37]]));
      p.flat("accent", any(disc(0.02, 0.45, 0.018), disc(-0.1, 0.42, 0.018), disc(0.12, 0.42, 0.016)), 4);
      p.lit("accentDark", disc(-0.22, 0.34, 0.045));
      return;
    default:
      return;
  }
}

/** A leg from the hip: trouser thigh, then from the knee the shin and a knee boot with its foot forward. */
function leg(name: string, z: number, far: boolean): Part[] {
  const pants = far ? "pantsFar" : "pants";
  const boot = far ? "bootFar" : "boot";
  return [
    { name, parent: "hips", at: [0, 0], z, moves: true, shapes: [art(-0.1, -0.42, 0.1, 0.1, (p) => p.lit(pants, cap(0, 0, 0, -THIGH, 0.175)))] },
    {
      name: `${name}Knee`,
      parent: name,
      at: [0, -THIGH],
      z: z + 0.1,
      moves: true,
      shapes: [
        art(-0.12, -0.34, 0.22, 0.1, (p) => {
          p.lit(pants, cap(0, 0.02, 0, -0.14, 0.155));
          p.lit(boot, poly([[-0.085, -0.07], [0.08, -0.07], [0.08, -0.21], [0.15, -0.225], [0.185, -0.27], [0.19, -0.3], [-0.09, -0.3]]));
          p.lit(boot, box(-0.095, -0.115, 0.09, -0.05));
          p.flat("sole", box(-0.095, -0.32, 0.195, -0.29));
          if (!far) p.lit("brass", box(0.035, -0.215, 0.07, -0.19));
        }),
      ],
    },
  ];
}

/** An arm from the shoulder: sleeve to the elbow, then the forearm and a cuff (and, on the far arm, its glove). */
function arm(name: string, z: number, far: boolean): Part[] {
  const sleeve = far ? "sleeveFar" : "sleeve";
  return [
    { name, parent: "torso", at: [0, SHOULDER_Y], z, moves: true, shapes: [art(-0.1, -0.4, 0.1, 0.1, (p) => p.lit(sleeve, any(disc(0, 0, 0.085), cap(0, 0, 0, -UPPER_ARM, 0.14))))] },
    {
      name: `${name}Elbow`,
      parent: name,
      at: [0, -UPPER_ARM],
      z: z + 0.1,
      moves: true,
      shapes: [
        art(-0.1, -0.32, 0.1, 0.08, (p) => {
          p.lit(sleeve, cap(0, 0.02, 0, -0.17, 0.125));
          p.lit(far ? "accentDark" : "accent", cap(0, -0.15, 0, -0.2, 0.14));
          if (far) p.lit("gloveFar", disc(0, -FOREARM + 0.03, 0.062, 0.07));
        }),
      ],
    },
  ];
}

/** The tool in hand, in the hand's frame (the handle along +y, the striking edge on its +x side). */
function tool(kind: ToolKind): Shape {
  return art(-0.32, -0.2, 0.42, 0.97, (p) => {
    if (kind !== "sword") {
      p.lit("wood", cap(0, -0.17, 0, 0.61, 0.05));
      p.lit("leather", cap(0, -0.1, 0, 0.04, 0.06));
    }
    switch (kind) {
      case "pickaxe":
        p.lit("toolHead", poly([[-0.3, 0.5], [-0.15, 0.6], [0.0, 0.635], [0.2, 0.6], [0.32, 0.55], [0.4, 0.44], [0.28, 0.52], [0.15, 0.53], [-0.05, 0.56]]));
        p.lit("toolEdge", poly([[0.27, 0.565], [0.34, 0.52], [0.4, 0.44], [0.32, 0.49], [0.25, 0.52]]));
        p.lit("iron", disc(0, 0.58, 0.045, 0.055));
        return;
      case "axe":
        p.lit("toolHead", poly([[-0.06, 0.48], [0.04, 0.47], [0.14, 0.42], [0.2, 0.33], [0.25, 0.45], [0.25, 0.57], [0.21, 0.69], [0.14, 0.62], [0.04, 0.61], [-0.06, 0.62]]));
        p.flat("toolEdge", stroke([[0.21, 0.35], [0.245, 0.45], [0.245, 0.57], [0.21, 0.67]], 0.035));
        return;
      case "shovel":
        p.lit("iron", cap(0, 0.56, 0, 0.64, 0.06));
        p.lit("toolHead", poly([[-0.105, 0.63], [0.105, 0.63], [0.11, 0.82], [0.06, 0.9], [0, 0.92], [-0.06, 0.9], [-0.11, 0.82]]));
        p.flat("toolEdge", stroke([[0.1, 0.83], [0.05, 0.895], [0, 0.91], [-0.05, 0.895], [-0.1, 0.83]], 0.03));
        return;
      case "sword":
        p.lit("leather", cap(0, -0.09, 0, 0.07, 0.06));
        p.lit("brass", disc(0, -0.11, 0.04));
        p.lit("toolEdge", poly([[-0.036, 0.11], [0.036, 0.11], [0.03, 0.73], [0, 0.81], [-0.03, 0.73]]));
        p.flat("toolShade", box(-0.006, 0.14, 0.01, 0.66));
        p.lit("brass", cap(-0.12, 0.09, 0.12, 0.09, 0.05));
        return;
    }
  });
}

/** A crew rig: one hair style, hat, tool (or none) in the front hand, build, and whether a steampack is on their back. */
export function crewRig(hair: HairStyle, hat: Look["hat"], held: ToolKind | null, build: Look["body"] = "standard", pack = true): Part[] {
  const { w, h } = BUILDS[build];
  const coat = sized([[-0.17, -0.02], [-0.2, 0.15], [-0.205, 0.3], [-0.195, 0.48], [-0.16, 0.6], [-0.06, 0.64], [0.08, 0.62], [0.17, 0.56], [0.2, 0.45], [0.195, 0.3], [0.185, 0.15], [0.17, -0.02]], w, h);
  const tails = sized([[-0.18, 0.02], [-0.22, -0.15], [-0.27, -0.36], [-0.14, -0.39], [-0.02, -0.33], [0.03, -0.12], [0.08, 0.02]], w);
  return [
    { name: "hips", at: [0, HIP_Y], z: 0, moves: true, shapes: [] },
    // Leans from the hips; everything above hangs from it.
    { name: "torso", parent: "hips", at: [0, 0], z: 5, moves: true, shapes: [] },
    // Far side: arm, leg, the pack on the back (when worn).
    ...arm("armB", 1, true),
    ...leg("legB", 2, true),
    ...(pack ? packParts() : []),
    // The scarf, from the back of the collar: bends and streams out as they go.
    {
      name: "scarf",
      parent: "torso",
      at: [-0.08, 0.6],
      z: 4,
      spine: [-0.42, 0],
      shapes: [
        art(-0.47, -0.08, 0.1, 0.08, (p) => {
          p.lit("accent", poly([[0.03, 0.06], [-0.2, 0.055], [-0.38, 0.03], [-0.38, -0.03], [-0.2, -0.055], [0.03, -0.06]]));
          p.flat("accentDark", any(box(-0.44, 0.012, -0.38, 0.03), box(-0.45, -0.01, -0.38, 0.008), box(-0.44, -0.032, -0.38, -0.014)));
          p.lit("accent", disc(0, 0, 0.07, 0.06));
        }),
      ],
    },
    {
      name: "coat",
      parent: "torso",
      at: [0, 0],
      z: 6,
      moves: true,
      shapes: [
        art(-0.26, -0.06, 0.26, 0.76, (p) => {
          p.lit("coat", poly(coat));
          // A fold down the chest, the lapel, a pocket flap, a double row of brass buttons.
          p.flat("coat", stroke(sized([[0.03, 0.48], [0.05, 0.3], [0.02, 0.1]], w, h), 0.018), 1);
          p.flat("coat", stroke(sized([[0.06, 0.6], [0.15, 0.4], [0.18, 0.14]], w, h), 0.018), 0);
          p.flat("coat", box(-0.02 * w, 0.17 * h, 0.09 * w, 0.2 * h), 1);
          for (const y of [0.33, 0.22]) {
            p.lit("brass", disc(0.13 * w, y * h, 0.02));
            p.flat("brassDark", disc(0.07 * w, y * h, 0.018));
          }
          if (pack) {
            // The pack's strap over the shoulder and down the chest, a buckle on it.
            p.lit("strap", stroke(sized([[-0.2, 0.5], [-0.06, 0.58], [0.05, 0.52], [0.07, 0.42], [0.04, 0.12]], w, h), 0.045));
            p.lit("brass", box(0.04 * w, 0.345 * h, 0.085 * w, 0.375 * h));
          }
          // Belt and buckle.
          p.lit("leather", box(-0.19 * w, 0.02, 0.19 * w, 0.1));
          p.lit("brass", box(0.1 * w, 0.015, 0.2 * w, 0.105));
          p.flat("leather", box(0.125 * w, 0.04, 0.175 * w, 0.08), 1);
          // A high collar.
          p.lit("accent", poly(sized([[-0.13, 0.55], [-0.12, 0.68], [-0.01, 0.71], [0.09, 0.67], [0.1, 0.57], [0, 0.6]], w, h)));
        }),
      ],
    },
    // The coat's tails, below the belt over the near leg's thigh; they flap back as they run.
    {
      name: "tails",
      parent: "hips",
      at: [0, 0.04],
      z: 7,
      moves: true,
      shapes: [
        art(-0.34, -0.44, 0.13, 0.06, (p) => {
          p.lit("coat", poly(tails));
          p.flat("coat", box(-0.18 * w, -0.05, 0.06 * w, 0.0), 1);
          p.flat("coat", stroke([[-0.07 * w, -0.06], [-0.08 * w, -0.2], [-0.12 * w, -0.33]], 0.018), 1);
        }),
      ],
    },
    ...leg("legF", 6.5, false),
    { name: "head", parent: "torso", at: [0, NECK_Y], z: 8, moves: true, shapes: [head(hair, hat)] },
    ...arm("armF", 9, false),
    ...(held ? [{ name: "tool", parent: "armFElbow", at: [0.02, -FOREARM + 0.04], z: 9.2, moves: true, shapes: [tool(held)] } satisfies Part] : []),
    { name: "hand", parent: "armFElbow", at: [0, -FOREARM + 0.03], z: 9.3, moves: true, shapes: [art(-0.09, -0.09, 0.09, 0.09, (p) => p.lit("glove", disc(0, 0, 0.064, 0.072)))] },
  ];
}

/** The steampack on the back (a steel tank with brass bands and a gauge) and the flame under it, which the animator shows only while jetting. */
function packParts(): Part[] {
  return [
    {
      name: "pack",
      parent: "torso",
      at: [-0.33, 0],
      z: 3,
      moves: true,
      shapes: [
        art(-0.15, -0.06, 0.3, 0.68, (p) => {
          p.lit("steel", stroke([[0.06, 0.5], [0.09, 0.6], [0.16, 0.635], [0.26, 0.63]], 0.05));
          p.lit("tank", cap(0, 0.14, 0, 0.48, 0.23));
          p.lit("brass", box(-0.125, 0.42, 0.125, 0.46));
          p.lit("brass", box(-0.125, 0.16, 0.125, 0.2));
          // A pressure gauge: brass rim, a pale dial with ticks round its top, a brass needle (nothing like an eye).
          p.lit("brass", disc(0.02, 0.31, 0.06));
          p.flat("gauge", disc(0.02, 0.31, 0.042));
          p.flat("brassDark", any(box(-0.022, 0.31, -0.008, 0.326), box(0.012, 0.336, 0.028, 0.352), box(0.046, 0.31, 0.062, 0.326)));
          p.flat("brassDark", stroke([[0.012, 0.302], [0.05, 0.33]], 0.016));
          p.lit("brassDark", poly([[-0.06, 0.05], [0.06, 0.05], [0.08, -0.03], [-0.08, -0.03]]));
          p.lit("brass", box(-0.075, 0.04, 0.075, 0.075));
        }),
      ],
    },
    {
      name: "flame",
      parent: "pack",
      at: [0, -0.04],
      z: 2,
      moves: true,
      shapes: [
        { color: "flame", at: [0, -0.15], form: { kind: "disc", r: 0.075, ry: 0.17 } },
        { color: "flameCore", at: [0, -0.09], form: { kind: "disc", r: 0.035, ry: 0.1 } },
      ],
    },
  ];
}

const models = new Map<string, RigModel>();

/** The crew rig for a hair style, hat, held tool, build and pack (worn or not), shared by everyone dressed alike. */
export function crewModel(hair: HairStyle, hat: Look["hat"], held: ToolKind | null, build: Look["body"] = "standard", pack = true): RigModel {
  const key = `${hair}|${hat}|${held}|${build}|${pack}`;
  let m = models.get(key);
  if (!m) models.set(key, (m = new RigModel({ parts: crewRig(hair, hat, held, build, pack), glow: ["flame", "flameCore"] })));
  return m;
}

// ------------------------------------------------------------ colours

const SKINS: readonly Color[] = [
  [0.95, 0.8, 0.66],
  [0.86, 0.66, 0.5],
  [0.68, 0.48, 0.33],
  [0.46, 0.31, 0.21],
];
const HAIR_COLORS: readonly Color[] = [
  [0.1, 0.07, 0.05],
  [0.3, 0.19, 0.1],
  [0.52, 0.24, 0.11],
  [0.78, 0.62, 0.38],
  [0.62, 0.6, 0.58],
];

/** The tool's head and edge by what it's made of. */
const TIERS: Record<ToolTier, { head: Color; edge: Color }> = {
  wood: { head: [0.6, 0.42, 0.22], edge: [0.76, 0.55, 0.32] },
  stone: { head: [0.5, 0.52, 0.55], edge: [0.66, 0.68, 0.7] },
  iron: { head: [0.62, 0.66, 0.7], edge: [0.9, 0.92, 0.94] },
};

/** What a crew member looks like that isn't what they chose: skin and hair colour, fixed by their name. */
export function crewTraits(name: string): { skin: number; hair: number } {
  return { skin: Math.floor(idHash(name, 21) * SKINS.length), hair: Math.floor(idHash(name, 22) * HAIR_COLORS.length) };
}

/** Colours for a crew member: their coat in `hue` (0..1) with its opposite for the trim, their skin and hair, the tool's metal. */
export function crewPalette(hue: number, traits: { skin: number; hair: number }, tier: ToolTier = "wood"): Palette {
  const coat = hsl(hue, 0.42, 0.36);
  const accent = hsl(hue + 0.5, 0.5, 0.45);
  const skin = SKINS[traits.skin]!;
  const hair = HAIR_COLORS[traits.hair]!;
  const leather: Color = [0.36, 0.23, 0.12];
  const brass: Color = [0.82, 0.62, 0.3];
  const boot: Color = [0.17, 0.12, 0.09];
  const pants: Color = [0.3, 0.25, 0.2];
  const tank: Color = [0.44, 0.33, 0.27];
  return {
    coat,
    coatDark: scale(coat, 0.72),
    coatShade: scale(coat, 0.86),
    coatLight: mix(coat, WHITE, 0.22),
    sleeve: scale(coat, 0.86),
    sleeveLight: mix(coat, WHITE, 0.12),
    sleeveShade: scale(coat, 0.72),
    sleeveFar: scale(coat, 0.58),
    accent,
    accentDark: scale(accent, 0.7),
    accentLight: mix(accent, WHITE, 0.3),
    skin,
    skinDark: scale(skin, 0.82),
    blush: mix(skin, [0.85, 0.35, 0.3], 0.45),
    mouth: scale(skin, 0.5),
    eye: [0.07, 0.05, 0.04],
    glint: [0.95, 0.95, 0.9],
    hair,
    hairLight: mix(hair, WHITE, 0.25),
    hairDark: scale(hair, 0.7),
    pants,
    pantsFar: scale(pants, 0.65),
    pantsShade: scale(pants, 0.8),
    boot,
    bootLight: mix(boot, leather, 0.6),
    bootFar: scale(boot, 0.7),
    sole: [0.06, 0.05, 0.04],
    glove: leather,
    gloveLight: mix(leather, WHITE, 0.2),
    gloveFar: scale(leather, 0.65),
    leather,
    strap: [0.14, 0.1, 0.08],
    felt: [0.22, 0.16, 0.13],
    feltLight: [0.34, 0.26, 0.21],
    brass,
    brassDark: scale(brass, 0.68),
    tank,
    tankDark: scale(tank, 0.7),
    tankLight: mix(tank, WHITE, 0.25),
    steel: [0.62, 0.64, 0.66],
    iron: [0.3, 0.32, 0.35],
    glass: [0.5, 0.72, 0.8],
    gauge: [0.92, 0.88, 0.76],
    wood: [0.54, 0.35, 0.18],
    woodLight: [0.68, 0.47, 0.26],
    toolHead: TIERS[tier].head,
    toolShade: scale(TIERS[tier].head, 0.75),
    toolEdge: TIERS[tier].edge,
    // Above 1 so they bloom.
    flame: [3, 1.5, 0.45],
    flameCore: [4, 3.6, 2.4],
  };
}

// ------------------------------------------------------------ moving

/** What a character is doing, which picks the animation. */
export type CharacterMode = "ground" | "air" | "climb" | "swim" | "drift" | "crawl" | "helm";

export interface CharacterPose {
  mode: CharacterMode;
  facing: number;
  /** Speed along the ground (or across the screen in the character's own frame). */
  along: number;
  /** Speed toward the character's head: positive rising, negative falling. */
  rise: number;
  grounded: boolean;
  /**
   * Where the mouse is, relative to the shoulder, in the character's own
   * (unmirrored) frame: +X to their right, +Y toward their head. The body
   * faces it and the head and tool arm follow it. Null when too close to say.
   */
  aim: { x: number; y: number } | null;
  /**
   * Crawling (see GRIP): which way the rock they hold lies, in the same
   * unmirrored frame as `aim` — straight down with it under their feet, to one
   * side against a wall, up with it overhead. Their hands reach that way.
   */
  grip?: { x: number; y: number } | null;
  /** The tool in their hand (null: empty-handed, or holding a block or material). */
  tool: HeldTool | null;
  /** Swinging the tool over and over (left mouse held). */
  using: boolean;
  /** Thrown off balance (radians, + forward) by the ship they're aboard speeding up or slowing. */
  sway?: number;
  /** The steampack blasting: the jetpack's flame shows, and they hang from it. */
  jetting?: boolean;
}

/** Every animated angle (radians, + swings forward). */
interface Joints {
  lean: number;
  head: number;
  thighF: number;
  kneeF: number;
  thighB: number;
  kneeB: number;
  armF: number;
  elbowF: number;
  armB: number;
  elbowB: number;
  /** The tool in the front hand: 0 points the handle along the forearm's side. */
  wrist: number;
}

const REST: Joints = { lean: 0, head: 0, thighF: 0, kneeF: 0, thighB: 0, kneeB: 0, armF: 0.25, elbowF: 1.2, armB: -0.1, elbowB: 0.3, wrist: -1.3 };

/**
 * How a crew member moves: walking, jumping and landing, climbing,
 * swimming, drifting weightless, at the helm, hanging from the steampack;
 * the head and tool arm following the mouse, and tool swings over the top.
 * Each step gives the rig's pose, and which way they face and how squashed
 * they are from a landing (for whoever mirrors and squashes the rig).
 */
export class CrewAnimator {
  /** Which way they face: 1 right, -1 left. */
  facing = 1;
  /** How squashed the body is right now from a landing, 0..1. */
  squash = 0;
  build: Look["body"] = "standard";

  private readonly j: Joints = { ...REST };
  private time: number;
  private walkPhase = 0;
  private swimPhase = 0;
  private wasGrounded = true;
  private lastRise = 0;
  /** Seconds left of the landing crouch, and how hard it was. */
  private land = 0;
  private landPower = 0;
  /** Seconds into the current tool swing, or < 0 when not swinging. */
  private swingT = -1;
  /** Seconds into the current swing while the tool is held in use. */
  private useT = 0;
  /** Which way the mouse last put them facing. */
  private facingAim = 1;

  constructor(seed = Math.random()) {
    this.time = seed * 10;
  }

  /** Swing once (placing a block). A swing already under way finishes first. */
  swing(): void {
    if (this.swingT >= 0 && this.swingT < SWING_TIME * 0.7) return;
    this.swingT = 0;
  }

  step(dt: number, pose: CharacterPose): Pose {
    this.time += dt;
    // Face the mouse when there's somewhere to look; otherwise the way you're going.
    const aims = !!pose.aim && pose.mode !== "helm" && pose.mode !== "climb" && pose.mode !== "swim";
    if (aims && Math.abs(pose.aim!.x) > 0.15) this.facingAim = Math.sign(pose.aim!.x);
    this.facing = (aims ? this.facingAim : pose.facing) < 0 ? -1 : 1;
    // The aim's angle above straight ahead, in the mirrored body's own space.
    const aimAngle = aims ? clampTo(Math.atan2(pose.aim!.y, Math.abs(pose.aim!.x)), -1.4, 1.5) : 0;
    // The animation was tuned for a 1.7-tall figure: measure speeds in its body lengths.
    const along = pose.along * BODY_SCALE;
    const rise = pose.rise * BODY_SCALE;
    const speed = Math.abs(along);
    const run = Math.min(1, speed / (CHARACTER.walkSpeed * BODY_SCALE));

    // Landing: a quick crouch, harder after a long fall.
    if (pose.grounded && !this.wasGrounded && this.lastRise < -3) {
      this.land = 0.18;
      this.landPower = Math.min(1, -this.lastRise / 14);
    }
    this.wasGrounded = pose.grounded;
    this.lastRise = rise;
    this.land = Math.max(0, this.land - dt);

    const t: Joints = { ...REST };
    const breathe = Math.sin(this.time * 2.2);
    const jetting = !!pose.jetting && pose.mode !== "helm" && pose.mode !== "climb" && pose.mode !== "swim";
    if (jetting) {
      // Hanging from the pack: legs trailing a little apart, arms out for balance, a touch of lean back.
      const d = Math.sin(this.time * 7);
      t.thighF = 0.2 + 0.05 * d;
      t.kneeF = -0.25;
      t.thighB = -0.12 - 0.05 * d;
      t.kneeB = -0.2;
      t.armF = 0.55;
      t.elbowF = 0.4;
      t.armB = 0.7;
      t.elbowB = 0.35;
      t.lean = -0.12;
      t.head = 0.08;
    } else
      switch (pose.mode) {
        case "ground": {
          if (speed > 0.3) {
            this.walkPhase += dt * (3 + speed * 1.6);
            const s = Math.sin(this.walkPhase);
            const stride = 0.25 + 0.4 * run;
            t.thighF = s * stride;
            t.thighB = -s * stride;
            // The knee bends while that foot swings forward through the air.
            t.kneeF = -Math.max(0, Math.cos(this.walkPhase)) * (0.4 + 0.7 * run);
            t.kneeB = -Math.max(0, -Math.cos(this.walkPhase)) * (0.4 + 0.7 * run);
            t.armB = s * (0.2 + 0.45 * run);
            t.elbowB = 0.3 + 0.5 * run;
            t.armF = 0.25 - s * 0.15 * run;
            t.lean = -0.12 * run;
            t.head = 0.06 * run;
          } else {
            this.walkPhase = 0;
            t.armF += breathe * 0.03;
            t.armB += breathe * 0.04;
            t.head = breathe * 0.02;
          }
          break;
        }
        case "air": {
          // Tucked going up, reaching down for the ground coming down.
          const up = Math.max(0, Math.min(1, rise / 6));
          const down = Math.max(0, Math.min(1, -rise / 8));
          t.thighF = 0.2 + 0.7 * up + 0.1 * down;
          t.kneeF = -0.3 - 1.0 * up;
          t.thighB = -0.15 + 0.3 * up - 0.2 * down;
          t.kneeB = -0.5 - 0.4 * up + 0.3 * down;
          t.armB = 0.4 + 1.6 * up + 1.2 * down;
          t.elbowB = 0.3;
          t.armF = 0.6 + 0.6 * up + 0.4 * down;
          t.lean = 0.05 * down;
          break;
        }
        case "climb": {
          this.walkPhase += dt * (2 + Math.abs(rise) * 1.6);
          const s = Math.sin(this.walkPhase);
          t.armB = 2.6 + 0.4 * s;
          t.elbowB = 0.4 - 0.3 * s;
          t.armF = 2.6 - 0.4 * s;
          t.elbowF = 0.4 + 0.3 * s;
          t.thighF = 0.6 + 0.4 * s;
          t.kneeF = -1.0 - 0.3 * s;
          t.thighB = 0.6 - 0.4 * s;
          t.kneeB = -1.0 + 0.3 * s;
          t.head = 0.2;
          break;
        }
        case "swim": {
          this.swimPhase += dt * (2.5 + Math.hypot(along, rise));
          const k = Math.sin(this.swimPhase * 2.2);
          // Overarm stroke, arms half a turn apart; legs flutter.
          t.armF = this.swimPhase;
          t.armB = this.swimPhase + Math.PI;
          t.elbowF = t.elbowB = 0.3;
          t.thighF = 0.2 + 0.3 * k;
          t.thighB = 0.2 - 0.3 * k;
          t.kneeF = t.kneeB = -0.4;
          t.lean = -0.35;
          t.head = 0.3;
          break;
        }
        case "drift": {
          // Weightless: knees drawn up a little, everything drifting.
          const d = Math.sin(this.time * 1.3);
          t.thighF = 0.35 + 0.08 * d;
          t.kneeF = -0.7 - 0.1 * d;
          t.thighB = 0.1 - 0.08 * d;
          t.kneeB = -0.5 + 0.1 * d;
          t.armB = 0.5 + 0.15 * d;
          t.elbowB = 0.5;
          t.armF = 0.5 - 0.1 * d;
          t.lean = -0.1 * d;
          break;
        }
        case "crawl": {
          // Hands hold and feet do nothing (see GRIP): both arms reach for the rock, wherever it lies — under
          // the feet on top of it, out to one side against a wall, overhead underneath it — and go hand over
          // hand as you crawl. The legs only trail, tucked and drifting after them.
          const g = pose.grip ? Math.atan2(pose.grip.x * this.facing, -pose.grip.y) : 0;
          this.walkPhase += dt * (2 + Math.hypot(along, rise) * 1.2);
          const s = Math.sin(this.walkPhase);
          const d = Math.sin(this.time * 1.3);
          t.armF = g + 0.45 * s;
          t.elbowF = 0.5 - 0.3 * s;
          t.armB = g - 0.45 * s;
          t.elbowB = 0.5 + 0.3 * s;
          // The legs brace against it when it's off to one side, and float loose below when it's under the feet
          // or overhead — hence the sine, which is nothing at either end and most of it on a wall.
          const leg = Math.sin(g) * 0.9;
          t.thighF = leg + 0.22 + 0.06 * d;
          t.kneeF = -0.5 - 0.1 * d;
          t.thighB = leg - 0.08 - 0.06 * d;
          t.kneeB = -0.38 + 0.1 * d;
          t.lean = Math.sin(g) * 0.18;
          t.head = 0.08;
          break;
        }
        case "helm": {
          // Both hands forward on the levers, rocking a little with them.
          const r = Math.sin(this.time * 1.5) * 0.06;
          t.armF = 1.15 + r;
          t.elbowF = 0.45;
          t.armB = 1.15 - r;
          t.elbowB = 0.45;
          t.lean = -0.06;
          t.head = 0.04;
          break;
        }
      }

    // Landing crouch on top of whatever else is going on, and a squash of the whole body.
    let squash = 0;
    if (this.land > 0) {
      const c = Math.sin((this.land / 0.18) * Math.PI) * (0.4 + 0.6 * this.landPower);
      t.thighF += 0.7 * c;
      t.thighB += 0.5 * c;
      t.kneeF -= 1.3 * c;
      t.kneeB -= 1.1 * c;
      t.armB += 0.5 * c;
      t.lean -= 0.15 * c;
      squash = 0.14 * c;
    }
    this.squash += (squash - this.squash) * (1 - Math.exp(-dt * 30));
    // Thrown about by the ship speeding up or slowing under them.
    if (pose.sway) t.lean += clampTo(pose.sway, -0.35, 0.35);

    // Looking and reaching at the mouse: the head turns toward it and the tool arm points at it.
    if (aims) {
      t.head += clampTo(aimAngle * 0.55, -0.5, 0.6);
      t.armF = aimAngle + Math.PI / 2 - 0.15;
      t.elbowF = 0.25;
      t.wrist = AIM_WRIST;
    }

    // Ease toward the target pose. Swimming windmills freely, so it isn't eased.
    const ease = 1 - Math.exp(-dt * 18);
    for (const k of Object.keys(t) as (keyof Joints)[]) {
      if (pose.mode === "swim" && (k === "armF" || k === "armB")) this.j[k] = this.j[k] + wrapPi(t[k] - this.j[k]) * Math.min(1, ease * 3);
      else this.j[k] += (t[k] - this.j[k]) * ease;
    }

    // A tool swing takes over the front arm: wind up back over the shoulder, then strike at
    // what you're aiming at (or down in front). Held, it keeps swinging; a placement swings once.
    if (pose.using && aims) {
      this.useT = (this.useT + dt) % SWING_TIME;
      this.applySwing(this.useT / SWING_TIME, aimAngle);
    } else this.useT = 0;
    if (this.swingT >= 0) {
      this.swingT += dt;
      const u = Math.min(1, this.swingT / SWING_TIME);
      if (!pose.using) this.applySwing(u, aims ? aimAngle : -0.6);
      if (u >= 1) this.swingT = -1;
    }

    // On the ground the feet stay planted: the hips drop as the knees bend.
    const legDrop = (thigh: number, knee: number) => HIP_Y - (THIGH * Math.cos(thigh) + SHIN * Math.cos(thigh + knee));
    const planted = pose.mode === "ground" || this.land > 0;
    const drop = planted ? Math.min(legDrop(this.j.thighF, this.j.kneeF), legDrop(this.j.thighB, this.j.kneeB)) : 0;
    const bob = pose.mode === "ground" && speed > 0.3 ? Math.abs(Math.cos(this.walkPhase)) * 0.03 * run : 0;

    // The scarf hangs, swings as you go, and streams out flat as you fly; the coat's tails flap back as you run.
    const stream = Math.min(1, pose.mode === "drift" || pose.mode === "air" || jetting ? Math.hypot(along, rise) / 8 : speed / 10);
    const flutter = Math.sin(this.time * (8 + 4 * stream)) * (0.12 + 0.35 * stream);
    const flap = Math.sin(this.time * 11) * 0.05 * stream;

    const build = BUILDS[this.build];
    // In hand: put away at the helm, on a ladder and swimming, when the hands are full.
    const away = pose.mode === "helm" || pose.mode === "climb" || pose.mode === "swim";
    const flicker = jetting ? 1 : 0;
    return {
      hips: { y: -drop + bob + (pose.mode === "ground" && speed <= 0.3 ? breathe * 0.008 : 0) },
      torso: { rot: this.j.lean },
      tails: { rot: this.j.lean * 0.5 - 0.35 * run - 0.25 * stream + flap },
      pack: { x: -(build.w - 1) * 0.2, y: (build.h - 1) * 0.3 },
      flame: { sx: flicker * (0.85 + Math.random() * 0.3), sy: flicker * (0.75 + Math.random() * 0.5) },
      scarf: { y: NECK_Y * (build.h - 1), rot: 1.0 - 0.75 * stream + flutter * 0.4, bend: 0.6 - 0.25 * stream + flutter },
      head: { rot: this.j.head, y: NECK_Y * (build.h - 1) },
      legF: { rot: this.j.thighF },
      legFKnee: { rot: this.j.kneeF },
      legB: { rot: this.j.thighB },
      legBKnee: { rot: this.j.kneeB },
      armF: { rot: this.j.armF, y: SHOULDER_Y * (build.h - 1) },
      armFElbow: { rot: this.j.elbowF },
      armB: { rot: this.j.armB, y: SHOULDER_Y * (build.h - 1) },
      armBElbow: { rot: this.j.elbowB },
      tool: away ? { sx: 0, sy: 0 } : { rot: this.j.wrist },
    };
  }

  /**
   * Pose the tool arm `u` (0..1) of the way through a swing at `aimAngle`
   * (radians above straight ahead): wound back over the shoulder, then struck
   * through the aim point.
   */
  private applySwing(u: number, aimAngle: number): void {
    const base = aimAngle + Math.PI / 2;
    const raised = { arm: base + 1.6, elbow: 0.7, wrist: AIM_WRIST + 0.9 };
    const struck = { arm: base - 0.35, elbow: 0.1, wrist: AIM_WRIST - 0.3 };
    if (u < 0.45) {
      const a = smooth(u / 0.45);
      this.j.armF += (raised.arm - this.j.armF) * a;
      this.j.elbowF += (raised.elbow - this.j.elbowF) * a;
      this.j.wrist += (raised.wrist - this.j.wrist) * a;
    } else {
      const a = Math.min(1, smooth((u - 0.45) / 0.55) * 1.6);
      this.j.armF = raised.arm + (struck.arm - raised.arm) * a;
      this.j.elbowF = raised.elbow + (struck.elbow - raised.elbow) * a;
      this.j.wrist = raised.wrist + (struck.wrist - raised.wrist) * a;
      this.j.lean = Math.min(this.j.lean, -0.12 * Math.sin(a * Math.PI));
    }
  }
}

const smooth = (x: number) => x * x * (3 - 2 * x);
const clampTo = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));
const wrapPi = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
